import React from "react";
import { ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * PartnerLink — a small, reusable anchor for external partner brand mentions.
 *
 * All partner URLs and aria labels live here so future URL changes are
 * one-line edits.
 *
 *   <PartnerLink partner="finicity">Mastercard Finicity</PartnerLink>
 *   <PartnerLink partner="uptiq">Uptiq AI</PartnerLink>
 *
 * Props:
 *   partner: "finicity" | "uptiq"
 *   children: string | node — the visible partner name / lockup
 *   className: extra classes
 *   showGlyph: boolean — render a small external-link glyph on hover (default true)
 *   underline: "always" | "hover" | "never" — decoration behavior (default "hover")
 */
const PARTNERS = {
  finicity: {
    href: "https://www.mastercard.com/us/en/business/open-finance.html",
    label: "Mastercard Finicity",
  },
  uptiq: {
    href: "https://www.uptiq.ai/",
    label: "Uptiq AI",
  },
};

export const PartnerLink = ({
  partner,
  children,
  className,
  showGlyph = true,
  underline = "hover",
  ...rest
}) => {
  const p = PARTNERS[partner];
  if (!p) return <>{children}</>;

  const underlineClass =
    underline === "always"
      ? "underline underline-offset-4 decoration-brand-emerald/70"
      : underline === "never"
      ? "no-underline"
      : "no-underline hover:underline hover:decoration-brand-emerald/70 underline-offset-4";

  return (
    <a
      href={p.href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Visit ${p.label} website (opens in new tab)`}
      data-testid={`partner-link-${partner}`}
      className={cn(
        // Inline by default so wrapped partner names inside prose don't
        // introduce extra whitespace. When a caller passes className with
        // `!inline-flex` (e.g. Trust Bar lockup) that override still wins.
        "text-inherit transition-colors hover:text-brand-emerald focus-visible:text-brand-emerald focus-visible:outline-none",
        showGlyph ? "group/plink inline-flex items-baseline gap-1" : "inline",
        underlineClass,
        className,
      )}
      {...rest}
    >
      <span>{children}</span>
      {showGlyph && (
        <ExternalLink
          className="h-[0.85em] w-[0.85em] shrink-0 translate-y-[1px] opacity-0 transition-opacity duration-150 group-hover/plink:opacity-70 group-focus-visible/plink:opacity-70"
          strokeWidth={2}
          aria-hidden="true"
        />
      )}
    </a>
  );
};

/**
 * AutoLinkPartners — takes a plain string and returns a JSX fragment with any
 * "Mastercard Finicity" / "Finicity" / "Uptiq AI" / "Uptiq" occurrences wrapped
 * in PartnerLink. Used to avoid manually splitting every long description or
 * FAQ answer in the codebase.
 *
 *   <AutoLinkPartners text={item.body} />
 */
// Ordered longest → shortest so "Mastercard Finicity" wins before "Finicity",
// and "Uptiq AI" wins before "Uptiq". No trailing-word lookahead — we want
// the partner name to link even when followed by a hyphenated suffix
// (e.g. "Mastercard Finicity-powered"). The '-powered' portion stays as
// plain text following the anchor.
const PARTNER_RE = /(Mastercard Finicity|Finicity|Uptiq AI|Uptiq)/g;

export const AutoLinkPartners = ({ text, className }) => {
  if (!text || typeof text !== "string") return text ?? null;

  const parts = [];
  let last = 0;
  let m;
  let key = 0;
  while ((m = PARTNER_RE.exec(text)) !== null) {
    const start = m.index;
    const end = start + m[0].length;
    if (start > last) parts.push(text.slice(last, start));
    const token = m[0];
    const partner =
      token === "Mastercard Finicity" || token === "Finicity"
        ? "finicity"
        : "uptiq";
    parts.push(
      <PartnerLink
        key={`al-${key++}`}
        partner={partner}
        showGlyph={false}
        underline="hover"
        className={className}
      >
        {token}
      </PartnerLink>,
    );
    last = end;
  }
  if (last < text.length) parts.push(text.slice(last));

  // Return as a single span so the auto-linked output is one flex/inline
  // child (prevents parent containers with `flex justify-between` — e.g.
  // Shadcn AccordionTrigger — from separating the fragment's siblings).
  return <span>{parts}</span>;
};

export default PartnerLink;
